// notification.service.ts
import { Injectable, inject } from '@angular/core';
import {
  LocalNotifications,
  PendingResult,
  PermissionStatus
} from '@capacitor/local-notifications';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings
} from 'capacitor-native-settings';
import { getOccurrenceDurationMinutes } from '../utils/occurrence-duration';
import {
  getOccurrenceWindow,
  getSymmetricDurationMinutes,
  getSymmetricLabelKey,
  isMinuteDurationSymmetric
} from '../utils/occurrence-window';

export interface NotificationConfig {
  byHourDurations: number[]; // e.g. [2, 5, 7]
  reminderMinutes: number[]; // e.g. [10, 15, 20]
  allowedDuration: [number, number]; // e.g. [-5, +5]
}

export interface ScheduledNotification {
  id: number;
  title: string;
  body: string;
  actionTypeId?: string;
  scheduleAt: Date;
  sound?: string;
  extra: {
    [key: string]: any;
    id: string;
    type: 'reminder' | 'available';
    notificationAt: Date;
  };
}

export interface TimelineActivityRecurrence {
  /**
   * Notification Page Identifier
   */
  id: string;
  /**
   * Notification Page Title
   */
  title: string;
  /**
   * Notification delay duration
   */
  rrule_minute_duration: string;
  /**
   * Notification delay duration of each occurrence, aligned with valid_dates
   * e.g. [5, 10, 5]
   */
  rrule_minute_durations?: Array<number>;
  /**
   * 1 when each duration applies before and after the time (time - d -> time + d),
   * 0 or absent for the duration after the time only (SMART-5734)
   */
  is_minute_duration_symmetric?: number;
  /**
   * Recurrence rule reminder for push notification
   */
  rrule_minute_reminder: string;
  /**
   * Notification recurrence date & time
   * e.g. [["2025-09-11 18:00:00", "saved"], ["2025-09-11 20:00:00", ""], ["2025-09-11 22:30:00", ""]]
   */
  valid_dates: Array<Array<string>>;
  /**
   * Current Occurrence
   */
  occurrence: number;
  /**
   * All Occurrence
   */
  occurrences?: Array<number>;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private toastController = inject(ToastController);
  private translateService = inject(TranslateService);

  /**
   * Generate all notification times based on form submission and config
   */
  generateNotifications(
    formSubmittedAt: Date,
    activityInfo: {
      title: string;
      uuid: string;
    },
    config: NotificationConfig
  ): ScheduledNotification[] {
    const notifications: ScheduledNotification[] = [];
    for (const hourOffset of config.byHourDurations) {
      const activityTime = new Date(
        formSubmittedAt.getTime() + hourOffset * 60 * 60 * 1000
      );

      // 1. Reminder notifications
      for (const reminderMinute of config.reminderMinutes) {
        const reminderTime = new Date(
          activityTime.getTime() - reminderMinute * 60 * 1000
        );
        notifications.push({
          id: this.generateIdentifier(),
          title: `${activityInfo.title}`,
          body: `Available in ${reminderMinute} minutes`,
          actionTypeId: activityInfo.uuid,
          scheduleAt: reminderTime,
          extra: {
            formSubmittedAt,
            hourOffset,
            reminderMinute,
            id: activityInfo.uuid,
            notificationAt: reminderTime,
            type: 'reminder'
          }
        });
      }

      // 2. Activity available notification (based on allowedDuration[0])
      if (config.allowedDuration[0] < 0) {
        const availableTime = new Date(
          activityTime.getTime() + config.allowedDuration[0] * 60 * 1000
        );
        notifications.push({
          id: this.generateIdentifier(),
          title: `${activityInfo.title}`,
          body: `Activity is now available!`,
          actionTypeId: activityInfo.uuid,
          scheduleAt: availableTime,
          extra: {
            formSubmittedAt,
            id: activityInfo.uuid,
            type: 'available',
            notificationAt: availableTime
          }
        });
      }
    }

    return notifications;
  }

  /**
   * Check current notification permissions
   * @returns Promise<PermissionStatus> with display property indicating permission state
   */
  async checkPermissions(): Promise<PermissionStatus> {
    return await LocalNotifications.checkPermissions();
  }

  /**
   * Request notification permissions from the user
   * @returns Promise<PermissionStatus> with display property indicating permission state
   */
  async requestPermissions(): Promise<PermissionStatus> {
    return await LocalNotifications.requestPermissions();
  }

  /**
   * Handle permission checking and requesting with user feedback
   * @returns Promise<boolean> true if permissions are granted, false otherwise
   */
  async handlePermissions(): Promise<boolean> {
    // Check current permissions
    const permissionStatus = await this.checkPermissions();

    if (permissionStatus.display === 'denied') {
      this.showToast(
        'Notification permissions are denied. Please enable them in settings.'
      );
      setTimeout(() => {
        NativeSettings.open({
          optionAndroid: AndroidSettings.AppNotification,
          optionIOS: IOSSettings.AppNotification
        });
      }, 500);
      return false;
    }

    // Request permissions if not granted
    if (permissionStatus.display !== 'granted') {
      const requestResult = await this.requestPermissions();
      if (requestResult.display !== 'granted') {
        NativeSettings.open({
          optionAndroid: AndroidSettings.AppNotification,
          optionIOS: IOSSettings.AppNotification
        });
        this.showToast(
          'Notification permissions are required to schedule notifications.'
        );
        return false;
      }
    }

    return true;
  }

  /**
   * Reset all pending notifications
   * notifications	LocalNotificationDescriptor[]
   * cancel(options: CancelOptions) => Promise<void>
   * CancelOptions => { notifications: number[] }
   */
  async resetPendingNotifications() {
    const pending = await this.getPendingNotifications();
    if (pending.notifications.length < 1) return;
    await LocalNotifications.cancel({ notifications: pending.notifications });
  }

  /**
   * Schedule notifications with Capacitor
   * Checks and requests permissions if needed before scheduling
   */
  async scheduleNotifications(
    notifications: ScheduledNotification[]
  ): Promise<PendingResult> {
    const hasPermissions = await this.handlePermissions();
    if (!hasPermissions) {
      return { notifications: [] } as PendingResult; // Exit early if permissions are not granted
    }

    await LocalNotifications.schedule({
      notifications: notifications.map(n => ({
        id: n.id,
        title: n.title,
        body: n.body,
        sound: n.sound || 'default',
        schedule: { at: n.scheduleAt, allowWhileIdle: true },
        extra: n.extra
      }))
    });

    return await this.getPendingNotifications();
  }

  /**
   * Get all pending notifications
   * @returns Array of pending notifications or empty result if permissions are not granted
   */
  async getPendingNotifications(): Promise<PendingResult> {
    const hasPermissions = await this.handlePermissions();
    if (!hasPermissions) {
      // Return empty result if permissions are not granted
      return { notifications: [] };
    }
    return await LocalNotifications.getPending();
  }

  async showToast(title: string) {
    const toast = await this.toastController.create({
      message: title,
      duration: 3000,
      position: 'top',
      color: 'warning'
    });
    await toast.present();
  }

  /**
   * Convert notifications into scheduled notifications
   * On a page with the ± option, the "available" notification is sent when the
   * window opens (time - d) and each reminder before that, in device-local
   * time. Pages without the option are unchanged.
   */
  expandTimelineActivityRecurrences(
    notifications: TimelineActivityRecurrence[]
  ): ScheduledNotification[] {
    const result: ScheduledNotification[] = [];

    for (const notif of notifications) {
      // Use valid_dates directly instead of parsing RRULE
      if (!notif.valid_dates || notif.valid_dates.length === 0) continue;

      notif.valid_dates.forEach((occurrenceArr, dateIndex) => {
        // Parse the date string (format: '2025-09-11 18:00:00')
        const occurrenceDate = (occurrenceArr && occurrenceArr[0]) ?? undefined;
        if (!occurrenceDate) return;

        const activityTime = new Date(occurrenceDate);

        if (isNaN(activityTime.getTime())) {
          console.warn(`Invalid date format: ${occurrenceDate}`);
          return;
        }

        // Handle MINUTE_REMINDER as either array or single number
        const reminderMinutes =
          notif.rrule_minute_reminder && notif.rrule_minute_reminder !== '0'
            ? notif.rrule_minute_reminder
                .split(',')
                .map(s => parseInt(s.trim(), 10))
                .filter(n => !isNaN(n))
            : [];

        // MINUTE_DELAY of this occurrence
        const delayMinutes = getOccurrenceDurationMinutes(notif, dateIndex);

        // ± option: notifications follow the start of the window
        const symmetricWindow = isMinuteDurationSymmetric(notif)
          ? getOccurrenceWindow(notif, dateIndex)
          : undefined;
        const availableTime = symmetricWindow?.start ?? activityTime;
        const symmetricMinutes = symmetricWindow
          ? getSymmetricDurationMinutes(notif, dateIndex)
          : undefined;
        const symmetricLabelKey = getSymmetricLabelKey(
          symmetricMinutes,
          'NOTIFICATION_AVAILABLE_SYMMETRIC_ONE',
          'NOTIFICATION_AVAILABLE_SYMMETRIC'
        );

        // Create reminder notifications
        reminderMinutes.forEach(reminderMinute => {
          if (reminderMinute && reminderMinute > 0) {
            const reminderTime = new Date(
              availableTime.getTime() - reminderMinute * 60 * 1000
            );

            result.push({
              id: this.getOccurrenceNotificationIdentifier(
                notif.id,
                occurrenceDate,
                'reminder',
                reminderMinute
              ),
              title: '',
              // Singular text for 1 minute
              body: this.translateService.instant(
                reminderMinute === 1
                  ? 'NOTIFICATION_REMINDER_ONE'
                  : 'NOTIFICATION_REMINDER',
                {
                  item: notif.title,
                  x: reminderMinute
                }
              ),
              // body: '', //`Activity will be available in ${reminderMinute} minutes`,
              scheduleAt: reminderTime,
              sound: 'default',
              extra: {
                ...notif,
                dateIndex, // track which valid_date this relates to
                id: notif.id,
                notificationAt: reminderTime,
                type: 'reminder'
              }
            });
          }
        });

        // Create available notification
        result.push({
          id: this.getOccurrenceNotificationIdentifier(
            notif.id,
            occurrenceDate,
            'available'
          ),
          title: '',
          // Singular text for 1 minute, also when the duration was floored to 1
          // ± page: "± d minutes", and no ± for ±0
          body: symmetricLabelKey
            ? this.translateService.instant(symmetricLabelKey, {
                item: notif.title,
                x: symmetricMinutes
              })
            : symmetricMinutes === 0
            ? this.translateService.instant('NOTIFICATION_AVAILABLE', {
                item: notif.title
              })
            : delayMinutes
            ? this.translateService.instant(
                delayMinutes === 1
                  ? 'NOTIFICATION_AVAILABLE_DELAY_ONE'
                  : 'NOTIFICATION_AVAILABLE_DELAY',
                {
                  item: notif.title,
                  x: delayMinutes
                }
              )
            : this.translateService.instant('NOTIFICATION_AVAILABLE', {
                item: notif.title
              }),
          //body: '', //'Activity is now available',
          scheduleAt: availableTime,
          sound: 'default',
          extra: {
            ...notif,
            dateIndex,
            id: notif.id,
            notificationAt: availableTime,
            type: 'available'
          }
        });
      });
    }

    return result;
  }

  /**
   * Schedule the timeline notifications so each occurrence is pending once
   * - past notifications (up to the current minute) are skipped
   * - pending notifications no longer in the timeline are cancelled
   * - the others are scheduled with their stable identifier, which replaces
   *   the pending notification with the same identifier instead of adding one
   *
   * The local notification tray belongs to the timeline only: every pending
   * notification that is not in this timeline is cancelled, and an empty
   * timeline cancels them all. Another producer (e.g. generateNotifications)
   * must not schedule into this tray without changing this rule to cancel only
   * the timeline types ('reminder' and 'available').
   */
  async syncTimelineNotifications(
    recurrences: TimelineActivityRecurrence[]
  ): Promise<PendingResult | undefined> {
    if (!recurrences || recurrences.length < 1) {
      await this.resetPendingNotifications();
      return undefined;
    }

    const currentMinute = new Date();
    currentMinute.setSeconds(0, 0);

    const notificationsById = new Map<number, ScheduledNotification>();
    this.expandTimelineActivityRecurrences(recurrences)
      .filter(notification => {
        const notificationMinute = new Date(notification.extra.notificationAt);
        notificationMinute.setSeconds(0, 0);
        return notificationMinute > currentMinute;
      })
      .forEach(notification =>
        notificationsById.set(notification.id, notification)
      );

    const pendingNotifications = (await this.getPendingNotifications())
      .notifications;
    const staleNotifications = pendingNotifications.filter(
      pending => !notificationsById.has(pending.id)
    );
    if (staleNotifications.length > 0) {
      await LocalNotifications.cancel({
        notifications: staleNotifications.map(pending => ({ id: pending.id }))
      });
    }

    if (notificationsById.size < 1) return undefined;
    return await this.scheduleNotifications([...notificationsById.values()]);
  }

  /**
   * Stable identifier of a timeline notification, the same occurrence always
   * gets the same identifier (positive 32-bit integer, required by Android)
   */
  getOccurrenceNotificationIdentifier(
    pageId: string,
    occurrenceDate: string,
    type: 'reminder' | 'available',
    reminderMinute = 0
  ): number {
    const key = `${pageId}|${occurrenceDate}|${type}|${reminderMinute}`;

    // FNV-1a 32-bit hash
    let hash = 0x811c9dc5;
    for (let i = 0; i < key.length; i++) {
      hash ^= key.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }

    return hash >>> 1 || 1;
  }

  /**
   * Generate a random 9-digit numeric identifier
   */
  generateIdentifier(): number {
    // Ensure leading digit is not zero
    const min = 1_000_000_00; // 9-digit minimum (100000000)
    const max = 9_999_999_99; // 9-digit maximum (999999999)
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
